import React, { useEffect, useState } from 'react';

import {
  Stack,
  useMediaQuery,
  useTheme,
  Button,
  Typography,
  Box,
} from '@mui/material';

import { defaultCenter } from '../../../helpers/api/addresses';

import { GoogleMap } from '../../../components/GoogleMap';
import { LocationSearchFilterBar as FilterBar } from '../../../components/FilterBar';
import { FooterBar } from '../../../components/FooterBar';
import { useSearchResults } from '../providers/SearchResultsProvider';
import { useFilteredSearchResults } from '../providers/FilteredSearchResultsProvider';
import { useMappableVenues } from '../providers/MappableVenuesProvider';
import { SpacesList } from './SpacesList';
import { List, Map } from 'react-feather';
import { CategorySelector } from './CategorySelector';

export const SearchResults = () => {
  const theme = useTheme();
  const lgMedia = useMediaQuery(theme.breakpoints.up('lg'));
  const smMedia = useMediaQuery(theme.breakpoints.down('md'));
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [showMap, setShowMap] = useState(true);

  const buildMapWidth = () => {
    if (lgMedia) {
      return '33vw';
    }
    if (smMedia) {
      return '100vw';
    }

    return '50vw';
  };

  const mapStyle = {
    width: buildMapWidth(),
  };

  const showList = !smMedia || !showMap;

  useEffect(() => {
    if (smMedia) {
      setShowMap(false);
    } else {
      setShowMap(true);
    }
  }, [smMedia]);

  const {
    locationQuery,
    setLocationQuery,
    loading,
    totalSpaceCount,
    spaces: allSpaces,
    center,
    hasMore,
    loadMore,
    handleBoundsChange,
    isFetchingMore,
  } = useSearchResults();
  const {
    filteredSpaces,
    capacityFilterValues,
    setCapacityFilterValues,
    selectedCategory,
    setSelectedCategory,
  } = useFilteredSearchResults();
  const spacesCount =
    totalSpaceCount - (allSpaces.length - filteredSpaces.length);

  useEffect(() => {
    if (hasMore && filteredSpaces.length === 0) {
      loadMore();
    }
  }, [filteredSpaces.length, hasMore, loadMore]);

  const { mappableVenues } = useMappableVenues();

  // Reset isInitialLoad when location query changes
  useEffect(() => {
    setIsInitialLoad(true);
  }, [locationQuery]);

  return (
    <Box
      sx={{
        height: '100vh',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}
    >
      <Stack
        direction="column"
        alignItems="center"
        sx={{
          width: '100%',
          boxShadow: '0px -1px 0px 0px rgba(0, 0, 0, 0.1) inset',
        }}
      >
        <FilterBar
          locationQuery={locationQuery}
          onChangeLocationQuery={(queryString: string) =>
            setLocationQuery(queryString || '')
          }
          capacityFilterValues={capacityFilterValues}
          onSubmitCapacityFilter={setCapacityFilterValues}
          onCancelCapacityFilter={() =>
            setCapacityFilterValues({
              minGuests: 0,
              maxGuests: 0,
              allowSeated: false,
              allowStanding: false,
            })
          }
        />
        <CategorySelector
          selectedCategory={selectedCategory}
          setSelectedCategory={setSelectedCategory}
        />
      </Stack>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        sx={{
          padding: '10px 12px 10px 16px',
        }}
      >
        <Typography
          variant="h2"
          sx={{
            fontSize: '16px',
            lineHeight: '150%',
            fontWeight: 400,
          }}
        >
          <b>{spacesCount || filteredSpaces.length || 0}</b> Event Spaces
        </Typography>
        {smMedia && (
          <Button
            onClick={() => setShowMap(!showMap)}
            startIcon={showMap ? <List /> : <Map />}
            sx={{ color: 'inherit' }}
          >
            {showMap ? 'List' : 'Map'}
          </Button>
        )}
      </Stack>
      <Stack
        direction="row"
        sx={{
          flex: 1,
          overflow: 'hidden',
        }}
      >
        {showList && (
          <Stack
            justifyContent="space-between"
            sx={{
              padding: '24px 24px 0px 24px',
              overflowY: 'auto',
              width: {
                xs: '100vw',
                md: '50vw',
                lg: '67vw',
              },
            }}
          >
            <SpacesList
              spacesCount={spacesCount}
              loading={loading && !isFetchingMore}
              spaces={filteredSpaces || []}
            />
            {hasMore && (
              <Button
                onClick={loadMore}
                disabled={isFetchingMore}
                sx={{
                  opacity: isFetchingMore ? 0.7 : 1,
                  mt: 2,
                  mb: 2,
                }}
              >
                {isFetchingMore ? 'Loading more...' : 'Load More'}
              </Button>
            )}
            <FooterBar />
          </Stack>
        )}
        {showMap && mappableVenues && (
          <GoogleMap
            mapStyle={mapStyle}
            mappableItems={mappableVenues}
            center={
              isInitialLoad
                ? {
                    lat: center?.latitude || defaultCenter.lat,
                    lng: center?.longitude || defaultCenter.lng,
                  }
                : undefined
            }
            onBoundsChange={(bounds) => {
              setIsInitialLoad(false);
              handleBoundsChange(bounds);
            }}
          />
        )}
      </Stack>
    </Box>
  );
};
